import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { TablePager } from '@/components/ui';
import styles from './AuditPagination.module.css';

export interface AuditPaginationProps {
    /** Rows returned on THIS page (not the page_size). */
    rowCount: number;
    /** Server-reported total across every page. */
    total: number;
    /** Rows requested per page — the upper bound `rowCount` is clamped to. */
    pageSize: number;
    page: number;
    totalPages: number;
    onPrev: () => void;
    onNext: () => void;
}

/**
 * EAA-1821: spec's exact copy shape — "Hiển thị 5 / 218 bản ghi" — is
 * `rowCount` (this page's row count) over `total` (server `count`), not the
 * requested page_size: the last page of a 218-row, 20-per-page listing has
 * 18 rows, not 20, and the spec's example ("5 / 218") is itself a
 * short-last-page case. `pageSize` is the clamp, not the value: a server that
 * over-returns must not make the footer claim more rows than a page holds.
 *
 * The footer's own behaviour — genuinely `disabled` prev/next rather than
 * merely dimmed ones — lives in `packages/ui`'s `TablePager`; this component is
 * the copy and the arithmetic.
 */
export function AuditPagination({
    rowCount,
    total,
    pageSize,
    page,
    totalPages,
    onPrev,
    onNext,
}: AuditPaginationProps): ReactElement {
    const { t } = useTranslation();
    const shown = Math.min(rowCount, pageSize);

    const labels = useMemo(
        () => ({
            prev: t('console.audit.pagination.prev', { defaultValue: '← Trước' }),
            next: t('console.audit.pagination.next', { defaultValue: 'Sau →' }),
            page: t('console.audit.pagination.page', {
                defaultValue: 'Trang {{page}}/{{totalPages}}',
                page,
                totalPages,
            }),
        }),
        [t, page, totalPages]
    );

    return (
        <TablePager
            className={`portal-console-pagination ${styles.pager}`}
            page={page}
            totalPages={totalPages}
            onPrev={onPrev}
            onNext={onNext}
            labels={labels}
            summary={t('console.audit.pagination.showing', {
                defaultValue: 'Hiển thị {{shown}} / {{total}} bản ghi',
                shown,
                total,
            })}
        />
    );
}
